import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

const ClientPortfolio = () => {
  const [expandedCase, setExpandedCase] = useState(null);

  useEffect(() => {
    document.title = 'Client Portfolio | Multicolor International';
  }, []);

  // Clients organized: Multinationals (with logos) FIRST, then Local Pakistani brands
  const multinationals = [
    { name: 'Coca Cola', logo: '/clients/cocacola.svg' },
    { name: 'Unilever', logo: '/clients/unilever.svg' },
    { name: 'Mondelez', logo: '/clients/mondelez.svg' },
    { name: 'Magnum', logo: '/clients/magnum.png' },
    { name: 'Blueband', logo: '/clients/blueband.svg' },
    { name: 'Engro FrieslandCampina', logo: '/clients/campina.svg' }
  ];

  const locals = [
    { name: "K&N's", logo: null },
    { name: 'Engro Foods', logo: null },
    { name: 'Gourmet Foods', logo: null },
    { name: 'Millat Tractors', logo: null },
    { name: 'Hico Ice Cream', logo: null },
    { name: 'Ittehad Textiles', logo: null },
    { name: 'Cola Next', logo: null },
    { name: 'Sabroso', logo: null },
    { name: 'Varioline', logo: null },
    { name: 'Wagha Border', logo: null },
    { name: 'Seth Muhammad Tufail Company', logo: null },
    { name: 'National Highway Authority', logo: null }
  ];

  const clients = [...multinationals, ...locals];

  const caseStudies = [
    {
      id: 1,
      client: 'Food Distribution Company',
      title: 'Durable Barcode Label System for Refrigerated Products',
      challenge: 'A food distribution company needed barcode labels that could withstand harsh cold-chain conditions. Existing labels faded, peeled off, or became unreadable due to temperature fluctuations, moisture, and constant handling during transportation and storage.',
      solution: 'Our team developed a specialized industrial labeling solution using temperature-resistant adhesives, moisture-proof materials, high-durability barcode printing technology, and extensive testing under refrigeration and freezing conditions. The labels were designed to remain scannable and intact throughout the product lifecycle, from warehouse storage to retail display.',
      deliverables: ['Industrial Labels', 'Barcode Printing', 'Material Testing', 'Packaging Solutions'],
      results: [
        'Improved barcode scan accuracy',
        'Reduced inventory tracking errors',
        'Increased operational efficiency',
        'Long-lasting performance in cold-storage environments',
        'Significant reduction in relabeling costs'
      ]
    },
    {
      id: 2,
      client: "K&N's",
      title: "Strategic Corporate Gift & Branding Program for K&N's",
      challenge: "K&N's required premium corporate gifts and branded workplace installations that reflected the company's professional image while strengthening relationships with distributors, partners, and internal stakeholders.",
      solution: "We designed and produced a complete branded merchandise and workplace branding package, including executive gift boxes, custom-branded promotional products, premium employee appreciation kits, corporate wall-mounted branding displays, and office signage and visual branding elements. Every item was developed according to K&N's brand guidelines to ensure consistency across all touchpoints.",
      deliverables: ['Corporate Gifts', 'Promotional Products', 'Wall Branding Solutions', 'Signage', 'Brand Identity Implementation'],
      results: [
        'Stronger brand visibility across corporate locations',
        'Positive stakeholder and employee engagement',
        'Enhanced professional presentation during corporate events',
        'Consistent brand identity across gifts and workplace environments',
        'Successful rollout across multiple departments and partner networks'
      ]
    },
    {
      id: 3,
      client: 'National Highway Authority',
      title: 'Large-Scale Signage & Advertising',
      challenge: 'Required high-visibility outdoor signage for nationwide highway infrastructure projects that could withstand harsh weather conditions while maintaining clarity and visibility for years.',
      solution: 'Produced weather-resistant hoarding and flex signs with UV-protected printing for long-term durability. Implemented reflective materials and safety indicators meeting national standards for highway signage.',
      deliverables: ['Highway Signage', 'Reflective Materials', 'Safety Indicators', 'Information Boards'],
      results: [
        'Successfully installed across multiple provinces',
        'Exceeded durability expectations',
        'Improved highway safety and visibility',
        'Long-term cost savings through extended lifespan',
        'Met all national highway authority standards'
      ]
    }
  ];

  return (
    <div className="pt-24">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl sm:text-6xl font-bold mb-6" data-testid="portfolio-heading">
              Trusted By
              <br />
              <span className="gradient-text">Industry Leaders</span>
            </h1>
            <p className="text-xl text-[#5B5B5B] max-w-3xl mx-auto">
              Partnering with Fortune 500 companies and leading brands across Pakistan, USA, and Canada
            </p>
          </motion.div>
        </div>
      </section>

      {/* Client Logo Wall */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Our Clients</h2>
            <p className="text-lg text-[#5B5B5B]">
              Building lasting partnerships with industry leaders
            </p>
          </motion.div>

          {/* Multinational Brands */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-12"
          >
            <div className="flex items-center justify-center gap-4 mb-8">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#5B5B5B]">Multinational Brands</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 justify-items-center">
              {multinationals.map((client, index) => (
                <motion.div
                  key={client.name}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="w-full max-w-[200px] h-28 bg-white border border-gray-200 rounded-xl flex items-center justify-center p-4 hover:shadow-lg hover:border-[#1B3A6D]/30 transition-all duration-300 group"
                  data-testid={`client-logo-${client.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                >
                  {client.logo ? (
                    <img
                      src={client.logo}
                      alt={client.name}
                      className="max-h-14 max-w-full object-contain filter grayscale opacity-60 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-300"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div
                    className={`${client.logo ? 'hidden' : 'flex'} items-center justify-center text-center font-bold text-[#1B3A6D] text-sm group-hover:scale-105 transition-transform px-2`}
                  >
                    {client.name}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Local Pakistani Brands */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center justify-center gap-4 mb-8">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#5B5B5B]">Local Brands & Institutions</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 justify-items-center">
              {locals.map((client, index) => (
                <motion.div
                  key={client.name}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.04 }}
                  className="w-full max-w-[240px] h-28 bg-white border border-gray-200 rounded-xl flex items-center justify-center p-4 hover:shadow-lg hover:border-[#1B3A6D]/30 transition-all duration-300 group"
                  data-testid={`client-logo-${client.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                >
                  {client.logo ? (
                    <img
                      src={client.logo}
                      alt={client.name}
                      className="max-h-14 max-w-full object-contain filter grayscale opacity-60 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-300"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div
                    className={`${client.logo ? 'hidden' : 'flex'} items-center justify-center text-center font-bold text-[#1B3A6D] text-base group-hover:scale-105 transition-transform px-2`}
                  >
                    {client.name}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" data-testid="case-studies-heading">
              Case Studies
            </h2>
            <p className="text-lg text-[#5B5B5B]">
              Real results for real businesses
            </p>
          </motion.div>

          <div className="space-y-6">
            {caseStudies.map((study, index) => (
              <motion.div
                key={study.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-2xl border border-gray-200 overflow-hidden"
                data-testid={`case-study-${study.id}`}
              >
                <div
                  className="p-8 cursor-pointer"
                  onClick={() => setExpandedCase(expandedCase === study.id ? null : study.id)}
                >
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1">
                      <div className="text-sm font-bold text-[#1B3A6D] uppercase tracking-wide mb-2">
                        Case Study {String(study.id).padStart(2, '0')} • {study.client}
                      </div>
                      <h3 className="text-2xl font-bold mb-2">{study.title}</h3>
                    </div>
                    <button
                      className="p-2 hover:bg-gray-100 rounded-full transition flex-shrink-0"
                      data-testid={`case-study-toggle-${study.id}`}
                    >
                      {expandedCase === study.id ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
                    </button>
                  </div>
                </div>

                {expandedCase === study.id && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="px-8 pb-8 space-y-6 border-t border-gray-100"
                    data-testid={`case-study-content-${study.id}`}
                  >
                    <div className="pt-6">
                      <h4 className="font-bold text-lg mb-2 text-[#1B3A6D]">Client Challenge</h4>
                      <p className="text-[#5B5B5B] leading-relaxed">{study.challenge}</p>
                    </div>

                    <div>
                      <h4 className="font-bold text-lg mb-2 text-[#1B3A6D]">Multicolor Solution</h4>
                      <p className="text-[#5B5B5B] leading-relaxed">{study.solution}</p>
                    </div>

                    <div>
                      <h4 className="font-bold text-lg mb-3 text-[#1B3A6D]">Results</h4>
                      <ul className="space-y-2">
                        {study.results.map((item, i) => (
                          <li key={i} className="flex items-start text-[#5B5B5B]">
                            <span className="w-2 h-2 bg-[#00A651] rounded-full mr-3 mt-2 flex-shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-bold text-lg mb-3 text-[#1B3A6D]">Services Provided</h4>
                      <div className="flex flex-wrap gap-2">
                        {study.deliverables.map((item, i) => (
                          <span
                            key={i}
                            className="px-3 py-1.5 bg-[#1B3A6D]/10 text-[#1B3A6D] rounded-full text-sm font-medium"
                          >
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Served */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Industries We Serve</h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { name: 'Food & Beverage', icon: '🍽️' },
              { name: 'FMCG', icon: '📦' },
              { name: 'Manufacturing', icon: '🏭' },
              { name: 'Retail', icon: '🛍️' },
              { name: 'Corporate', icon: '🏢' },
              { name: 'Education', icon: '🎓' },
              { name: 'Healthcare', icon: '⚕️' },
              { name: 'Government', icon: '🏛️' }
            ].map((industry, index) => (
              <motion.div
                key={industry.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                className="service-card text-center"
                data-testid={`industry-${industry.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                <div className="text-4xl mb-3">{industry.icon}</div>
                <div className="font-semibold">{industry.name}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ClientPortfolio;
